import { useState } from "react";
import {
	Box,
	Typography,
	Paper,
	Checkbox,
	Chip,
	Button,
	Tabs,
	Tab,
	Select,
	MenuItem,
	InputAdornment,
	TextField,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import type { PropertyMappings } from "../../../../shared/PropertyMappings.types";
import type { GlobalPropertyMappingSelectorViewModel } from "./GlobalPropertyMappingSelector.types";
import type { PropertyOption, PropertySortOption } from "./GlobalPropertyMappingSelector.types";

type MobilePropertyTab = "properties" | "map-to";

function MobilePropertyRow({
	property,
	isSelected,
	onToggleSelect,
}: {
	property: PropertyOption;
	isSelected: boolean;
	onToggleSelect: (name: string, shiftKey: boolean) => void;
}) {
	return (
		<Paper
			elevation={1}
			onClick={() => onToggleSelect(property.name, false)}
			sx={{
				p: 1,
				mb: 0.5,
				bgcolor: isSelected ? "primary.50" : "background.paper",
				userSelect: "none",
				cursor: "pointer",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center" }}>
				<Checkbox size="small" checked={isSelected} tabIndex={-1} disableRipple sx={{ p: 0, mr: 0.5 }} />
				<Typography variant="body2" fontWeight={700} noWrap>
					{property.name}
				</Typography>
			</Box>
			{property.examples?.slice(0, 3).map((ex, i) => (
				<Typography key={i} variant="caption" color="text.secondary" display="block" noWrap sx={{ pl: 3.5 }}>
					{ex}
				</Typography>
			))}
		</Paper>
	);
}

function MobileFieldCard({
	fieldKey,
	label,
	assignedProperties,
	selectedCount,
	onAssignSelected,
	onUnassign,
}: {
	fieldKey: keyof PropertyMappings;
	label: string;
	assignedProperties: PropertyOption[];
	selectedCount: number;
	onAssignSelected: (fieldKey: keyof PropertyMappings) => void;
	onUnassign: (fieldKey: keyof PropertyMappings, propertyName: string) => void;
}) {
	return (
		<Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
			<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
				{label}
			</Typography>

			{assignedProperties.length > 0 && (
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
					{assignedProperties.map((p) => (
						<Chip
							key={p.name}
							label={p.name}
							size="small"
							color="primary"
							variant="outlined"
							onDelete={() => onUnassign(fieldKey, p.name)}
							sx={{ fontSize: "0.75rem" }}
						/>
					))}
				</Box>
			)}

			{assignedProperties.length === 0 && (
				<Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 0.5 }}>
					No properties mapped
				</Typography>
			)}

			{selectedCount > 0 && (
				<Button size="small" variant="contained" fullWidth onClick={() => onAssignSelected(fieldKey)}>
					Assign Selected ({selectedCount})
				</Button>
			)}
		</Paper>
	);
}

export function MobilePropertyMappingLayout({ vm }: { vm: GlobalPropertyMappingSelectorViewModel }) {
	const [activeTab, setActiveTab] = useState<MobilePropertyTab>("properties");

	const showMoreLabel = (() => {
		if (vm.showMoreFields) return "Show Less";
		const withMappings =
			vm.hiddenFieldsWithMappingsCount > 0 ? ` (${vm.hiddenFieldsWithMappingsCount} with mappings)` : "";
		return `Show ${vm.hiddenFieldsCount} More${withMappings}`;
	})();

	return (
		<Box className="global-property-mapping-selector">
			<Box sx={{ mb: 2, flexShrink: 0 }} color="text.secondary">
				<Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
					Select properties from your uploaded files, then switch to Map To and tap a field to assign them.
				</Typography>
			</Box>
			{vm.showAutoGuessNotice && (
				<Typography variant="body2" className="auto-guess-notice" sx={{ flexShrink: 0 }}>
					We&apos;ve automatically guessed some property mappings for you. Please review and adjust as needed.
				</Typography>
			)}

			<Tabs
				value={activeTab}
				onChange={(_, v) => setActiveTab(v)}
				sx={{ mb: 1, minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5, textTransform: "none" } }}
			>
				<Tab label={`Properties (${vm.unassignedProperties.length})`} value="properties" />
				<Tab
					label={vm.selectedProperties.size > 0 ? `Map To (${vm.selectedProperties.size} selected)` : "Map To"}
					value="map-to"
				/>
			</Tabs>

			{activeTab === "properties" && (
				<Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
					{/* Header controls */}
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1, flexWrap: "wrap" }}>
						<Select
							size="small"
							value={vm.sortOption}
							onChange={(e: SelectChangeEvent) => vm.setSortOption(e.target.value as PropertySortOption)}
							sx={{ fontSize: "0.75rem", height: 28 }}
						>
							<MenuItem value="name-asc">A → Z</MenuItem>
							<MenuItem value="name-desc">Z → A</MenuItem>
						</Select>
						{vm.selectedProperties.size > 0 && (
							<Chip
								label={`${vm.selectedProperties.size} selected`}
								size="small"
								color="primary"
								onDelete={vm.clearSelection}
							/>
						)}
					</Box>

					<TextField
						size="small"
						placeholder="Filter properties..."
						value={vm.filterText}
						onChange={(e) => vm.setFilterText(e.target.value)}
						fullWidth
						sx={{ mb: 1 }}
						slotProps={{
							input: {
								endAdornment: vm.filterText ? (
									<InputAdornment position="end">
										<Chip
											label="Clear"
											size="small"
											onClick={() => vm.setFilterText("")}
											onDelete={() => vm.setFilterText("")}
											sx={{ height: 20, fontSize: "0.7rem" }}
										/>
									</InputAdornment>
								) : undefined,
							},
						}}
					/>

					<Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
						{vm.unassignedProperties.length === 0 && (
							<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
								All properties assigned
							</Typography>
						)}
						{vm.unassignedProperties.length > 0 && vm.filteredUnassignedProperties.length === 0 && (
							<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
								No matches
							</Typography>
						)}
						{vm.filteredUnassignedProperties.map((property) => (
							<MobilePropertyRow
								key={property.name}
								property={property}
								isSelected={vm.selectedProperties.has(property.name)}
								onToggleSelect={vm.handlePropertyClick}
							/>
						))}
					</Box>
				</Box>
			)}

			{activeTab === "map-to" && (
				<Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Job Properties ({vm.visibleFields.length})
					</Typography>
					{vm.visibleFields.map((field) => (
						<MobileFieldCard
							key={field.key}
							fieldKey={field.key}
							label={field.label}
							assignedProperties={vm.selectedValues[field.key] || []}
							selectedCount={vm.selectedProperties.size}
							onAssignSelected={vm.assignSelectedToField}
							onUnassign={vm.unassignPropertyFromField}
						/>
					))}
					{(vm.hiddenFieldsCount > 0 || vm.showMoreFields) && (
						<Button onClick={vm.toggleShowMore} variant="text" size="small" sx={{ mt: 1 }}>
							{showMoreLabel}
						</Button>
					)}
				</Box>
			)}
		</Box>
	);
}
